import React, { useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * MediaGallery Component
 * 
 * A reusable component that displays media items in a horizontal scrollable list
 * with the ability to expand them in a modal with navigation.
 * 
 * @example
 * ```jsx
 * const mediaItems = [
 *   {
 *     type: 'image',
 *     url: 'image-url',
 *     username: 'user123',
 *     impactPoints: '2000',
 *     caption: 'Optional caption'
 *   },
 *   {
 *     type: 'video',
 *     url: 'video-url',
 *     thumbnail: 'thumbnail-url',
 *     username: 'user456',
 *     impactPoints: '3000',
 *     caption: 'Video caption'
 *   }
 * ];
 * 
 * <MediaGallery 
 *   mediaItems={mediaItems}
 *   title="Activity Title"
 *   showScrollArrows={true}
 * />
 * ```
 */

// Media Modal Component
const MediaModal = ({ isOpen, onClose, mediaItems, initialIndex = 0, title }) => {
    const [activeIndex, setActiveIndex] = useState(initialIndex);

    const currentMedia = mediaItems?.[activeIndex];
    const totalItems = mediaItems?.length || 0;

    const goToNext = () => {
        setActiveIndex((prev) => (prev + 1) % totalItems);
    };

    const goToPrevious = () => {
        setActiveIndex((prev) => (prev - 1 + totalItems) % totalItems);
    };

    const goToIndex = (index) => {
        setActiveIndex(index);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft') {
            goToPrevious();
        } else if (e.key === 'ArrowRight') {
            goToNext();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    React.useEffect(() => {
        setActiveIndex(initialIndex);
    }, [initialIndex]);

    if (!isOpen || !mediaItems || mediaItems.length === 0) return null;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4"
            onClick={onClose}
        >
            <div
                className="bg-grayBgNormal rounded-lg w-[44rem] min-h-[70vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-800 truncate">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Media Content */}
                <div className="flex-1 relative flex items-center justify-center p-4">
                    {/* Left Arrow */}
                    <button
                        onClick={goToPrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-grayBgNormal rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Media Display */}
                    <div className="relative w-full h-full flex items-center justify-center">
                        {mediaItems.map((item, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-300 ${index === activeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                    }`}
                            >
                                {item.type === 'video' ? (
                                    <div className="relative w-full h-full bg-gray-200 rounded-lg overflow-hidden">
                                        <img
                                            src={item.thumbnail || item.url}
                                            alt={item.caption || 'Video thumbnail'}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-16 h-16 bg-grayBgNormal rounded-full flex items-center justify-center shadow-lg">
                                                <svg className="w-8 h-8 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <img
                                        src={item.url}
                                        alt={item.caption || 'Image'}
                                        className="w-full h-full object-contain rounded-lg"
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Right Arrow */}
                    <button
                        onClick={goToNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-grayBgNormal rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {/* Footer with Media Info and Dots */}
                <div className="p-4 border-t">
                    {/* Media Info */}
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold text-textSecondary">
                            {currentMedia.impactPoints} IP
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-textSecondary">{currentMedia.username}</span>
                        </div>
                    </div>

                    {/* Caption */}
                    {currentMedia.caption && (
                        <p className="text-sm text-gray-700 mb-3">{currentMedia.caption}</p>
                    )}

                    {/* Pagination Dots */}
                    <div className="flex justify-center gap-2">
                        {mediaItems.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToIndex(index)}
                                className={`w-2 h-2 rounded-full transition-colors ${index === activeIndex ? 'bg-blue-500' : 'bg-gray-300 hover:bg-gray-400'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.getElementById('modal-root'));
};

// Individual Media Item Component
const MediaItem = ({ item, onClick }) => {
    const { type, url, thumbnail, username, impactPoints, caption } = item;

    return (
        <div
            className="relative flex-shrink-0 w-48 bg-gray-100 rounded-lg overflow-hidden border border-borderUnderline cursor-pointer hover:shadow-md transition-shadow"
            onClick={onClick}
        >
            {/* Media Content */}
            <div className="relative w-full h-32">
                {type === 'video' ? (
                    <div className="relative w-full h-full bg-gray-200">
                        <img
                            src={thumbnail || url}
                            alt={caption || 'Video thumbnail'}
                            className="w-full h-full object-cover"
                        />
                        {/* Video Play Button - Centered */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 bg-grayBgNormal rounded-full flex items-center justify-center shadow-lg">
                                <svg className="w-5 h-5 text-gray-600 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                ) : (
                    <img
                        src={url}
                        alt={caption || 'Image'}
                        className="w-full h-full object-cover"
                    />
                )}

                {/* Bottom Overlay - Impact Points and Username */}
                <div className="absolute bottom-2 left-2 right-2">
                    <div className="flex justify-between items-center">
                        {/* Impact Points */}
                        <span className="bg-black bg-opacity-50 text-textSecondary text-xs px-2 py-1 rounded">
                            {impactPoints} IP
                        </span>

                        {/* Username */}
                        <div className="flex items-center gap-1">
                            <span className="bg-black bg-opacity-50 text-textSecondary text-xs px-2 py-1 rounded">
                                {username}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Expand Button for Images */}
                {type === 'image' && (
                    <div className="absolute top-2 right-2">
                        <button className="bg-grayBgNormal bg-opacity-80 p-1 rounded hover:bg-opacity-90 transition-colors">
                            <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            {/* Optional Caption - Only show if caption exists */}
            {caption && (
                <div className="mt-2 px-1">
                    <p className="text-xs text-secondaryText leading-tight">
                        {caption}
                    </p>
                </div>
            )}
        </div>
    );
};

// Main MediaGallery Component
const MediaGallery = ({
    mediaItems = [],
    title = "Media Gallery",
    showScrollArrows = true,
    containerId = 'default',
    className = ""
}) => {
    const [modalState, setModalState] = useState({
        isOpen: false,
        initialIndex: 0
    });

    const openModal = (mediaIndex) => {
        setModalState({
            isOpen: true,
            initialIndex: mediaIndex
        });
    };

    const closeModal = () => {
        setModalState({
            isOpen: false,
            initialIndex: 0
        });
    };

    if (!mediaItems || mediaItems.length === 0) return null;

    return (
        <div className={`w-full ${className}`}>
            {/* Media Container */}
            <div className="w-full relative">
                {/* Left Arrow */}
                {showScrollArrows && mediaItems.length > 3 && (
                    <button
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-grayBgNormal rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                        onClick={() => {
                            const container = document.getElementById(`media-container-${containerId}`);
                            if (container) {
                                container.scrollBy({ left: -200, behavior: 'smooth' });
                            }
                        }}
                    >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}

                {/* Right Arrow */}
                {showScrollArrows && mediaItems.length > 3 && (
                    <button
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-grayBgNormal rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                        onClick={() => {
                            const container = document.getElementById(`media-container-${containerId}`);
                            if (container) {
                                container.scrollBy({ left: 200, behavior: 'smooth' });
                            }
                        }}
                    >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                )}

                {/* Media Items */}
                <div
                    id={`media-container-${containerId}`}
                    className={`flex gap-3 overflow-x-auto pb-2 scrollbar-hide ${showScrollArrows && mediaItems.length > 3 ? 'px-8' : ''
                        }`}
                >
                    {mediaItems.map((item, index) => (
                        <MediaItem
                            key={index}
                            item={item}
                            onClick={() => openModal(index)}
                        />
                    ))}
                </div>
            </div>

            {/* Media Modal */}
            <MediaModal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                mediaItems={mediaItems}
                initialIndex={modalState.initialIndex}
                title={title}
            />
        </div>
    );
};

export default MediaGallery; 